"use client";

import { AgentModal } from "@zapier/agents";
import { CanvasModal } from "@zapier/canvases";
import { ChatbotModal } from "@zapier/chatbots";
import {
  FolderTreeItem,
  HydratedAsset,
  isAgentAsset,
  isCanvasAsset,
  isChatbotAsset,
  isInterfaceAsset,
  isTableAsset,
  isZapAsset,
  useSplitTreatments,
  Zap,
} from "@zapier/client";
import { InterfaceModal } from "@zapier/interfaces";
import {
  AssetDeleteModal,
  BulkSelectAction,
  CopyToAnotherAccountModal,
  FolderModal,
  Table,
  ZapModal,
} from "@zapier/shared";
import { TableModal } from "@zapier/tables";
import { AssetTypes, FOLDER_TRASH_ID } from "@zapier/utils";
import { useTranslations } from "@zapier/i18n";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import { BetaColumnHeader, SortDescriptor } from "@zapier/design-system";

import { useMultiAssetModalStates } from "../../hooks/useMultiAssetModalStates";
import { AssetRowAgent } from "./AssetRow/AssetRowAgent";
import { AssetRowCanvas } from "./AssetRow/AssetRowCanvas";
import { AssetRowChatbot } from "./AssetRow/AssetRowChatbot";
import { AssetRowFallback } from "./AssetRow/AssetRowFallback";
import { AssetRowInterface } from "./AssetRow/AssetRowInterface";
import { AssetRowTable } from "./AssetRow/AssetRowTable";
import { AssetRowZap } from "./AssetRow/AssetRowZap";
import { TrashDeleteModalContext } from "./AssetRow/TrashDeleteModalContext";
import styles from "./AssetTable.module.css";
import FolderRow from "./FolderRow/FolderRow";

const DynamicFolderModals = dynamic(
  () => import("../FolderModals").then((module_) => module_.FolderModals),
  { ssr: false },
);

const DynamicZapModals = dynamic(
  () => import("@zapier/shared").then((module_) => module_.ZapModals),
  { ssr: false },
);

const DynamicTableModals = dynamic(
  () => import("@zapier/tables").then((module_) => module_.TableModals),
  { ssr: false },
);

const DynamicInterfacesModals = dynamic(
  () =>
    import("@zapier/interfaces").then((module_) => module_.InterfacesModals),
  { ssr: false },
);

const DynamicCanvasModals = dynamic(
  () => import("@zapier/canvases").then((module_) => module_.CanvasModals),
  { ssr: false },
);

const DynamicChatbotModals = dynamic(
  () => import("@zapier/chatbots").then((module_) => module_.ChatbotModals),
  { ssr: false },
);

const DynamicAgentModals = dynamic(
  () => import("@zapier/agents").then((module_) => module_.AgentModals),
  { ssr: false },
);

interface AssetTableProps {
  ariaLabel: string;
  bulkSelectItems?: BulkSelectAction[];
  folders: FolderTreeItem[];
  /**
   * Absolute depth of the current folder (used to convert row-relative `folderLevel` into an absolute depth).
   */
  currentFolderDepth?: number;
  folderId?: string;
  assets: HydratedAsset[];
  isLoading?: boolean;
  showCheckboxes?: boolean;
  showLocationColumn?: boolean;
  setCurrentModal?: (modal: FolderModal | undefined) => void;
  setCurrentFolder?: (folder: FolderTreeItem | undefined) => void;
  selectableEntities?: { id: string }[];
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  isSortEnabled?: boolean;
  /** Opts the Status column out of sorting while the rest of the table keeps it. */
  isStatusSortEnabled?: boolean;
  showLastRun?: boolean;
  sortDescriptor?: SortDescriptor;
  /** Tooltip to show when sorting is disabled */
  sortDisabledTooltip?: string;
  isCopyToAnotherAccountEnabled?: boolean;
  bulkActionsEmptyStateMessage?: string;
}

/** Keys used to translate the `folder-table-row` column labels. */
export type ColumnLabelKey =
  | "columns.status"
  | "columns.apps"
  | "columns.location"
  | "columns.previous-folder"
  | "columns.last-modified"
  | "columns.deleted"
  | "columns.last-run";

export const getColumns = ({
  t,
  isSortEnabled,
  isStatusSortEnabled = true,
  sortDisabledTooltip,
  showLocationColumn = true,
  showLastRun = false,
  isTrashView = false,
}: {
  t: (key: ColumnLabelKey) => string;
  isSortEnabled: boolean;
  isStatusSortEnabled?: boolean;
  sortDisabledTooltip?: string;
  showLocationColumn?: boolean;
  showLastRun?: boolean;
  isTrashView?: boolean;
}) =>
  [
    // Status and Apps are meaningless in trash, so they're hidden there.
    ...(!isTrashView
      ? [
          {
            label: t("columns.status"),
            className: styles.statusColumn,
            allowsSorting: isSortEnabled && isStatusSortEnabled,
            sortKey: "status",
            sortDisabledTooltip: isSortEnabled
              ? undefined
              : sortDisabledTooltip,
          },
          {
            label: t("columns.apps"),
            className: styles.appsColumn,
          },
        ]
      : []),
    ...(showLocationColumn
      ? [
          {
            label: isTrashView
              ? t("columns.previous-folder")
              : t("columns.location"),
            // Only the trash index sorts this column, and it does so server-side.
            allowsSorting: isTrashView && isSortEnabled,
            sortKey: "location",
            className: styles.locationColumn,
          },
        ]
      : []),
    {
      label: isTrashView ? t("columns.deleted") : t("columns.last-modified"),
      className: styles.lastModifiedColumn,
      allowsSorting: isSortEnabled,
      sortKey: "lastModified",
      sortDisabledTooltip: isSortEnabled ? undefined : sortDisabledTooltip,
    },
    ...(showLastRun
      ? [{ label: t("columns.last-run"), className: styles.lastRunColumn }]
      : []),
  ] as BetaColumnHeader[];

export function AssetTable({
  folderId,
  ariaLabel,
  folders,
  assets,
  currentFolderDepth,
  isLoading,
  showCheckboxes,
  showLocationColumn = true,
  bulkSelectItems,
  selectableEntities,
  onSortChange,
  isSortEnabled = false,
  isStatusSortEnabled = true,
  showLastRun = false,
  sortDescriptor,
  sortDisabledTooltip,
  isCopyToAnotherAccountEnabled = true,
  bulkActionsEmptyStateMessage,
}: AssetTableProps) {
  const t = useTranslations("folder-table-row");
  const [copyAssets, setCopyAssets] = useState<HydratedAsset[]>([]);
  const isCopyToAnotherAccountAllowed =
    isCopyToAnotherAccountEnabled && folderId !== FOLDER_TRASH_ID;
  const isTrashView = folderId === FOLDER_TRASH_ID;
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();
  const showTrashRowActions = isTrashView && isZapAndCanvasUnifiedTrashEnabled;
  // A row can't safely render its own confirmation modal (it's inside the DataTable's
  // row collection), so rows request one via context and this single instance, kept
  // outside <Table>, serves every row.
  const [pendingDeleteAssetId, setPendingDeleteAssetId] = useState<string>();

  const {
    currentModal,
    setCurrentModal,
    approvalType,
    setApprovalType,
    currentZap,
    setCurrentZap,
    currentCanvas,
    setCurrentCanvas,
    currentTable,
    setCurrentTable,
    currentInterface,
    setCurrentInterface,
    currentFolder,
    setCurrentFolder,
    currentChatbot,
    setCurrentChatbot,
    currentAgent,
    setCurrentAgent,
  } = useMultiAssetModalStates();

  const handleCopyAssetToAnotherAccount = useCallback(
    (asset: HydratedAsset) => {
      setCurrentFolder(undefined);
      setCopyAssets([asset]);
      setCurrentModal(FolderModal.CopyToAnotherAccount);
    },
    [setCopyAssets, setCurrentFolder, setCurrentModal],
  );

  const copyModalFolderIds = currentFolder?.id ? [currentFolder.id] : undefined;
  const copyModalAssets = copyModalFolderIds ? [] : copyAssets;

  // Use this to conditionally render the modals
  // so we aren't doing extra work for assets we don't need to display
  const hasAssetType = useMemo(() => {
    const hasType = (assetType: AssetTypes) => {
      return assets.some((asset) => asset.type === assetType);
    };

    return {
      hasZap: hasType(AssetTypes.Zap),
      hasCanvas: hasType(AssetTypes.Canvas),
      hasInterface: hasType(AssetTypes.Interface),
      hasTable: hasType(AssetTypes.Table),
      hasChatbot: hasType(AssetTypes.Chatbot),
      hasFolder: folders?.length > 0,
      hasAgent: hasType(AssetTypes.Agent),
    };
  }, [assets, folders]);

  return (
    <>
      <TrashDeleteModalContext.Provider
        value={{ requestDeleteForever: setPendingDeleteAssetId }}
      >
        <Table
          allowSortByTitle={isSortEnabled}
          ariaLabel={ariaLabel}
          columns={getColumns({
            t,
            isSortEnabled,
            isStatusSortEnabled,
            sortDisabledTooltip,
            showLocationColumn,
            showLastRun,
            isTrashView: folderId === FOLDER_TRASH_ID,
          })}
          isLoading={isLoading}
          showOwner={true}
          // No share action wired on these rows (onClickShare is never provided),
          // so don't reserve share-button width — keeps the Options column tight
          // and consistent with the other asset tables.
          showShareButton={false}
          folderZapTableRow={true}
          showCheckboxes={showCheckboxes}
          bulkSelectActions={bulkSelectItems}
          bulkActionsEmptyStateMessage={bulkActionsEmptyStateMessage}
          selectableEntities={selectableEntities}
          className={styles.assetTable}
          onSortChange={onSortChange}
          sortDescriptor={sortDescriptor}
          titleSortKey="name"
          sortDisabledTooltip={sortDisabledTooltip}
        >
          {folders.map((folder, index) => (
            <FolderRow
              folder={folder}
              folderLevel={0}
              currentFolderDepth={currentFolderDepth}
              folderZapTableRow={true}
              showLocationColumn={showLocationColumn}
              showLastRun={showLastRun}
              isTrashView={isTrashView}
              setCurrentModal={setCurrentModal}
              setCurrentFolder={setCurrentFolder}
              key={index}
              includeSelectionColumn={!!showCheckboxes}
            />
          ))}
          {assets.map((asset, index) => {
            if (isTableAsset(asset)) {
              return (
                <AssetRowTable
                  allowSelection={showCheckboxes}
                  key={index}
                  table={asset}
                  setCurrentModal={setCurrentModal}
                  setCurrentTable={setCurrentTable}
                  folderId={folderId}
                  showLocationColumn={showLocationColumn}
                  showLastRun={showLastRun}
                  onCopyToAnotherAccount={
                    isCopyToAnotherAccountAllowed
                      ? (selectedInterface) =>
                          handleCopyAssetToAnotherAccount(
                            selectedInterface as unknown as HydratedAsset,
                          )
                      : undefined
                  }
                />
              );
            }
            if (isInterfaceAsset(asset)) {
              return (
                <AssetRowInterface
                  allowSelection={showCheckboxes}
                  key={index}
                  interfaceObj={asset}
                  setCurrentModal={setCurrentModal}
                  setCurrentInterface={setCurrentInterface}
                  folderId={folderId}
                  showLocationColumn={showLocationColumn}
                  showLastRun={showLastRun}
                  onCopyToAnotherAccount={
                    isCopyToAnotherAccountAllowed
                      ? (selectedCanvas) =>
                          handleCopyAssetToAnotherAccount(
                            selectedCanvas as unknown as HydratedAsset,
                          )
                      : undefined
                  }
                />
              );
            }
            if (isCanvasAsset(asset)) {
              return (
                <AssetRowCanvas
                  allowSelection={showCheckboxes}
                  key={index}
                  canvas={asset}
                  setCurrentModal={setCurrentModal}
                  setCurrentCanvas={setCurrentCanvas}
                  folderId={folderId}
                  showLocationColumn={showLocationColumn}
                  showLastRun={showLastRun}
                  onCopyToAnotherAccount={
                    isCopyToAnotherAccountAllowed
                      ? (selectedAgent) =>
                          handleCopyAssetToAnotherAccount(
                            selectedAgent as unknown as HydratedAsset,
                          )
                      : undefined
                  }
                />
              );
            }
            if (isChatbotAsset(asset)) {
              return (
                <AssetRowChatbot
                  allowSelection={showCheckboxes}
                  key={index}
                  chatbot={asset}
                  setCurrentModal={setCurrentModal}
                  setCurrentChatbot={setCurrentChatbot}
                  folderId={folderId}
                  showLocationColumn={showLocationColumn}
                  showLastRun={showLastRun}
                  onCopyToAnotherAccount={
                    isCopyToAnotherAccountAllowed
                      ? (selectedAgent) =>
                          handleCopyAssetToAnotherAccount(
                            selectedAgent as unknown as HydratedAsset,
                          )
                      : undefined
                  }
                />
              );
            }
            if (isAgentAsset(asset)) {
              return (
                <AssetRowAgent
                  allowSelection={showCheckboxes}
                  agent={asset}
                  folderId={folderId}
                  key={index}
                  setCurrentAgent={setCurrentAgent}
                  setCurrentModal={setCurrentModal}
                  showLocationColumn={showLocationColumn}
                  showLastRun={showLastRun}
                  onCopyToAnotherAccount={
                    isCopyToAnotherAccountAllowed
                      ? (selectedAgent) =>
                          handleCopyAssetToAnotherAccount(
                            selectedAgent as unknown as HydratedAsset,
                          )
                      : undefined
                  }
                />
              );
            }
            if (isZapAsset(asset)) {
              return (
                <AssetRowZap
                  key={index}
                  zap={asset}
                  apps={[]}
                  setCurrentModal={setCurrentModal}
                  setCurrentZap={setCurrentZap}
                  setApprovalType={setApprovalType}
                  folderId={folderId}
                  showCheckboxes={showCheckboxes}
                  showLocationColumn={showLocationColumn}
                  showLastRun={showLastRun}
                  onCopyToAnotherAccount={
                    isCopyToAnotherAccountAllowed
                      ? handleCopyAssetToAnotherAccount
                      : undefined
                  }
                />
              );
            }
            return (
              <AssetRowFallback
                includeSelectionColumn={!!showCheckboxes}
                key={index}
                asset={asset}
                folderId={folderId}
                showLocationColumn={showLocationColumn}
                showLastRun={showLastRun}
              />
            );
          })}
        </Table>

        {showTrashRowActions && (
          <AssetDeleteModal
            selectedAssetIds={
              pendingDeleteAssetId ? [pendingDeleteAssetId] : []
            }
            isOpen={!!pendingDeleteAssetId}
            setIsOpen={(isOpen) => {
              if (!isOpen) {
                setPendingDeleteAssetId(undefined);
              }
            }}
            folderId={folderId}
          />
        )}

        {hasAssetType.hasFolder && (
          <DynamicFolderModals
            currentModal={currentModal as FolderModal}
            setCurrentModal={setCurrentModal}
            currentFolder={currentFolder}
            setCurrentFolder={setCurrentFolder}
          />
        )}
        <CopyToAnotherAccountModal
          isOpen={currentModal === FolderModal.CopyToAnotherAccount}
          setIsOpen={(isOpen) => {
            if (!isOpen) {
              setCurrentModal(undefined);
              setCurrentFolder(undefined);
              setCopyAssets([]);
            }
          }}
          assets={copyModalAssets}
          copyEntryPoint="row_kebab"
          folderIds={copyModalFolderIds}
        />
        {hasAssetType.hasZap && (
          <DynamicZapModals
            currentModal={currentModal as ZapModal}
            setCurrentModal={setCurrentModal}
            currentZaps={currentZap ? [currentZap as Zap] : []}
            setCurrentZap={setCurrentZap}
            folderId={folderId}
            approvalType={approvalType}
            setApprovalType={setApprovalType}
          />
        )}
        {hasAssetType.hasTable && (
          <DynamicTableModals
            currentModal={currentModal as TableModal}
            setCurrentModal={setCurrentModal}
            currentTable={currentTable}
            setCurrentTable={setCurrentTable}
            folderId={folderId}
          />
        )}
        {hasAssetType.hasInterface && (
          <DynamicInterfacesModals
            currentModal={currentModal as InterfaceModal}
            setCurrentModal={setCurrentModal}
            currentInterface={currentInterface}
            setCurrentInterface={setCurrentInterface}
            folderId={folderId}
          />
        )}
        {hasAssetType.hasCanvas && (
          <DynamicCanvasModals
            currentModal={currentModal as CanvasModal}
            setCurrentModal={setCurrentModal}
            currentCanvas={currentCanvas}
            setCurrentCanvas={setCurrentCanvas}
            folderId={folderId}
          />
        )}
        {hasAssetType.hasChatbot && (
          <DynamicChatbotModals
            currentModal={currentModal as ChatbotModal}
            setCurrentModal={setCurrentModal}
            currentChatbot={currentChatbot}
            setCurrentChatbot={setCurrentChatbot}
            folderId={folderId}
          />
        )}
        {hasAssetType.hasAgent && (
          <DynamicAgentModals
            currentModal={currentModal as AgentModal}
            setCurrentModal={setCurrentModal}
            currentAgent={currentAgent}
            setCurrentAgent={setCurrentAgent}
            folderId={folderId}
          />
        )}
      </TrashDeleteModalContext.Provider>
    </>
  );
}
